import { useRef, useState, type FormEvent } from "react";
import { AccountPanel } from "../components/AccountPanel";
import { Dashboard } from "../components/Dashboard";
import { Modal } from "../components/Modal";
import { PinPad } from "../components/PinPad";
import { MODE_INFO, OPS, type Mode, type Op } from "../content/modes";
import { itemById } from "../content/items";
import { ninjaById } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import { accuracy } from "../game/mastery";
import { availableModes } from "../game/modes";
import { QUESTION_TYPES } from "../game/questions/types";
import { timingByTier } from "../game/stats";
import { mathRandomRng, randInt } from "../game/rng";
import { trialFor } from "../game/trials";
import type { StoreActions, SyncStatus } from "../hooks/useStore";
import {
  ADD_MAX_OPTIONS,
  PRACTICE_MINUTE_OPTIONS,
  TABLE_MAX_OPTIONS,
  type HistoryEntry,
  type Settings,
} from "../save/schema";
import { liveChildren, viewOf, type Store } from "../save/store";
import { exportFilename, exportSave, importSave } from "../save/transfer";
import "./Parents.css";

type Ranks = Record<Mode, number>;

interface Props {
  store: Store;
  status: SyncStatus;
  actions: StoreActions;
  cloudAvailable: boolean;
  /** Start a placement check for this child. */
  onPlacement: (childId: string) => void;
  onSignIn: () => void;
  onPrivacy: () => void;
  onBack: () => void;
  /** Stays true while a grown-up moves between Parents, sign-in and privacy. */
  unlocked: boolean;
  onUnlock: () => void;
}

const OP_LABELS: Record<Op, string> = {
  add: "Adding",
  sub: "Taking away",
  mul: "Times",
  div: "Sharing",
};

export function historyLine(h: HistoryEntry): string {
  const label = MODE_INFO[h.mode].label;
  const coins = h.coins ? `, +${h.coins} coins` : "";
  const passes = h.passes ? `, ${h.passes} passed` : "";
  if (h.kind === "practice")
    return `${h.d}: ${label} practice, ${h.score} of ${h.tried}${passes}${coins}`;
  if (h.kind === "battle") {
    const foe = ninjaById(h.enemy ?? "")?.name ?? "a ninja";
    const who = ninjaById(h.fighter ?? "")?.name ?? "a ninja";
    const used = h.itemsUsed?.length
      ? `, used ${h.itemsUsed.map((id) => itemById(id)?.name ?? id).join(" and ")}`
      : "";
    return `${h.d}: ${label} battle, ${who} v ${foe}, ${h.won ? "won" : "lost"}, ${h.score} of ${h.tried}${passes}${used}${coins}`;
  }
  const spec = h.rankIndex === undefined ? null : trialFor(h.mode, h.rankIndex);
  if (!spec) return `${h.d}: ${label}, ${h.score} of ${h.tried}${passes} (${h.seconds / 60} min)`;
  const name = RANKS[spec.rankIndex]!.name;
  return `${h.d}: ${label}, ${name} trial, ${h.score} (needs ${spec.passMark})${h.passed ? ", passed" : ""}${passes}${coins}`;
}

/** "7+8" → "7 + 8" */
export function factText(key: string): string {
  return key.replace(/([+−×÷])/g, " $1 ");
}

export function makeGate(rng = mathRandomRng) {
  const a = randInt(rng, 6, 9);
  const b = randInt(rng, 6, 9);
  return { a, b, answer: a * b };
}

interface Draft {
  name: string;
  settings: Settings;
  ranks: Ranks;
}

export function Parents({
  store,
  status,
  actions,
  cloudAvailable,
  onPlacement,
  onSignIn,
  onPrivacy,
  onBack,
  unlocked,
  onUnlock,
}: Props) {
  const [gate] = useState(() => makeGate());
  const [gateInput, setGateInput] = useState("");
  const [gateMsg, setGateMsg] = useState("");

  const kids = liveChildren(store);
  const [editId, setEditId] = useState(store.activeChildId ?? kids[0]!.id);
  const child = kids.find((c) => c.id === editId) ?? kids[0]!;
  const save = viewOf(child);

  const draftOf = (s: typeof save): Draft => ({
    name: s.name,
    settings: s.settings,
    ranks: s.rank,
  });
  const [draft, setDraft] = useState<Draft>(() => draftOf(save));
  const { name, settings, ranks } = draft;
  const setName = (v: string) => setDraft((d) => ({ ...d, name: v }));
  const setSettings = (f: (s: Settings) => Settings) =>
    setDraft((d) => ({ ...d, settings: f(d.settings) }));
  const setRanks = (f: (r: Ranks) => Ranks) => setDraft((d) => ({ ...d, ranks: f(d.ranks) }));

  const [resetArmed, setResetArmed] = useState(false);
  const [removeArmed, setRemoveArmed] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [notice, setNotice] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const signedIn = !!store.account;

  const historyLines = save.history.length
    ? save.history.slice(0, 10).map(historyLine)
    : ["Nothing played yet."];

  const skills = QUESTION_TYPES.flatMap((t) => {
    const acc = accuracy(save.mastery[t.id]);
    return acc === null ? [] : [{ t, acc, seen: save.mastery[t.id]!.seen }];
  })
    .sort((a, b) => a.acc - b.acc || b.seen - a.seen)
    .slice(0, 12);

  const missed = Object.entries(save.missed)
    .sort(([, a], [, b]) => b.n - a.n)
    .slice(0, 8);

  const timings = timingByTier(save.history);

  // Strands just switched on that start at Seedling: offer a placement check.
  const newlyOn = OPS.filter((op) => settings.ops[op] && !save.settings.ops[op] && ranks[op] === 0);

  const setPractice = (patch: Partial<Settings["practice"]>) =>
    setSettings((s) => ({ ...s, practice: { ...s.practice, ...patch } }));

  const tryUnlock = (e: FormEvent) => {
    e.preventDefault();
    if (Number(gateInput) !== gate.answer) {
      setGateMsg("That's not it. Try again.");
      return;
    }
    onUnlock();
  };

  const setOp = (op: Op, on: boolean) =>
    setSettings((s) => ({ ...s, ops: { ...s.ops, [op]: on } }));

  const saveNow = () => {
    const ops = { ...settings.ops };
    if (!OPS.some((k) => ops[k])) ops.add = true;
    actions.saveChild(child.id, { name, settings: { ...settings, ops }, ranks });
  };
  const saveAndBack = () => {
    saveNow();
    onBack();
  };
  const saveAndPlace = () => {
    saveNow();
    onPlacement(child.id);
  };

  /** Switch which child is being edited, keeping any changes made so far. */
  const editChild = (id: string) => {
    saveNow();
    const next = kids.find((c) => c.id === id);
    if (!next) return;
    setEditId(id);
    setDraft(draftOf(viewOf(next)));
    setResetArmed(false);
    setRemoveArmed(false);
    setNotice("");
  };

  const addChild = (e: FormEvent) => {
    e.preventDefault();
    const n = newName.trim();
    if (!n) return;
    saveNow();
    actions.addChild(n);
    setNewName("");
    setNotice(`Added ${n}. Choose them above to change their settings.`);
  };

  const doRemove = () => {
    if (!removeArmed) {
      setRemoveArmed(true);
      return;
    }
    const others = kids.filter((c) => c.id !== child.id);
    actions.removeChild(child.id);
    setRemoveArmed(false);
    setNotice(`Removed ${child.name || "that child"}.`);
    if (others[0]) {
      setEditId(others[0].id);
      setDraft(draftOf(viewOf(others[0])));
    }
  };

  const doReset = () => {
    if (!resetArmed) {
      setResetArmed(true);
      return;
    }
    setResetArmed(false);
    actions.resetAll();
    setNotice("Progress reset.");
  };

  const doExport = async () => {
    const text = exportSave(save);
    const filename = exportFilename(save.name);
    const file = new File([text], filename, { type: "application/json" });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Nine Scrolls progress" });
        setNotice("Progress shared.");
        return;
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    setNotice(`Saved ${filename}.`);
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportSave(save));
      setNotice("Progress copied to the clipboard.");
    } catch {
      setNotice("Couldn't copy. Try Export instead.");
    }
  };

  const applyImport = (text: string) => {
    const res = importSave(text);
    if (!res.ok) {
      setNotice(res.reason);
      return;
    }
    if (store.activeChildId !== child.id) actions.selectChild(child.id);
    actions.importSave(res.save);
    if (!child.synced) setDraft(draftOf(res.save));
    setNotice(
      child.synced
        ? `Added that progress to ${child.name || "this child"}.`
        : `Imported progress${res.save.name ? ` for ${res.save.name}` : ""}.`,
    );
    setPasteOpen(false);
    setPasteText("");
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    applyImport(await file.text());
    if (fileInput.current) fileInput.current.value = "";
  };

  if (!unlocked)
    return (
      <section className="screen parents">
        <h2 className="display parents-title">Parents</h2>
        <form className="panel gate" onSubmit={tryUnlock}>
          <label htmlFor="gateIn">
            Grown-ups only: what is {gate.a} × {gate.b}?
          </label>
          <input
            id="gateIn"
            type="number"
            inputMode="numeric"
            autoComplete="off"
            value={gateInput}
            onChange={(e) => setGateInput(e.target.value)}
          />
          <button type="submit" className="big display" style={{ marginTop: 12 }}>
            Unlock
          </button>
          <p className="hint" aria-live="polite">
            {gateMsg}
          </p>
          <button type="button" className="ghost" onClick={onBack}>
            Back
          </button>
        </form>
      </section>
    );

  return (
    <section className="screen parents">
      <h2 className="display parents-title">Parents</h2>
      <div className="parents-body">
        <div className="panel kids-bar">
          <div className="kids-tabs" role="group" aria-label="Choose a child">
            {kids.map((c) => (
              <button
                key={c.id}
                type="button"
                className="mini"
                aria-pressed={c.id === child.id}
                onClick={() => editChild(c.id)}
              >
                {c.id === child.id ? name || "Ninja" : c.name || "Ninja"}
              </button>
            ))}
          </div>
          <form className="add-kid" onSubmit={addChild}>
            <label htmlFor="newKid" className="visually-hidden">
              New child&apos;s name
            </label>
            <input
              id="newKid"
              type="text"
              maxLength={20}
              autoComplete="off"
              placeholder="New child's name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <button type="submit" className="mini">
              Add a child
            </button>
          </form>
        </div>

        <div className="parents-cols">
          <div className="panel">
            <label htmlFor="sName">Ninja&apos;s name</label>
            <input
              id="sName"
              type="text"
              maxLength={20}
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <fieldset className="checks-set">
              <legend>Maths to include</legend>
              <div className="checks">
                {OPS.map((op) => (
                  <label key={op}>
                    <input
                      type="checkbox"
                      checked={settings.ops[op]}
                      onChange={(e) => setOp(op, e.target.checked)}
                    />{" "}
                    {OP_LABELS[op]}
                  </label>
                ))}
              </div>
            </fieldset>
            {newlyOn.length > 0 && (
              <div className="new-strand">
                <p className="hint">
                  {newlyOn.map((op) => OP_LABELS[op]).join(" and ")}{" "}
                  {newlyOn.length > 1 ? "are" : "is"} new. Do a quick placement check so they
                  don&apos;t start at Seedling? Gold cards already earned stay gold.
                </p>
                <button type="button" className="mini" onClick={saveAndPlace}>
                  Save and start a placement check
                </button>
              </div>
            )}
            <p className="hint">
              Strands climb together: a strand can&apos;t get more than two ranks ahead of the
              others switched on. Switching one on at Seedling? Take the placement check so it
              starts at a fair rank.
            </p>
            <fieldset className="checks-set">
              <legend>Read-aloud</legend>
              <div className="checks">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.audio}
                    onChange={(e) => setSettings((s) => ({ ...s, audio: e.target.checked }))}
                  />{" "}
                  Read questions aloud
                </label>
              </div>
            </fieldset>
            <p className="hint">
              Uses the device&apos;s voice. Trial results note whether it was on.
            </p>
            <fieldset className="checks-set">
              <legend>Trials</legend>
              <div className="checks">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.trialBar}
                    onChange={(e) => setSettings((s) => ({ ...s, trialBar: e.target.checked }))}
                  />{" "}
                  Show the progress bar
                </label>
              </div>
            </fieldset>
            <p className="hint">
              Turn it off if watching the bar makes trials stressful. The score and clock still
              show, and the trial is the same either way.
            </p>
            <span className="panel-label">Picture PIN</span>
            <p className="hint">
              {child.pin
                ? "Set. They tap three ninjas to start playing."
                : "None. Add one if siblings play on each other's profiles."}
            </p>
            <div className="transfer-row">
              <button type="button" className="mini" onClick={() => setPinOpen(true)}>
                {child.pin ? "Change PIN" : "Set a PIN"}
              </button>
              {child.pin && (
                <button
                  type="button"
                  className="mini"
                  onClick={() => actions.setPin(child.id, null)}
                >
                  Remove PIN
                </button>
              )}
            </div>
          </div>

          <div className="panel">
            <span className="panel-label">Practice</span>
            <p className="hint">
              Trials are the same for every child. These settings shape Practice only.
            </p>
            <label htmlFor="sMin">Practice length</label>
            <select
              id="sMin"
              value={settings.practice.minutes}
              onChange={(e) => setPractice({ minutes: Number(e.target.value) })}
            >
              {PRACTICE_MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m === 0 ? "No clock (tap Done to finish)" : `${m} minute${m === 1 ? "" : "s"}`}
                </option>
              ))}
            </select>
            <label htmlFor="sAdd">Adding and taking away up to</label>
            <select
              id="sAdd"
              value={settings.practice.addMax}
              onChange={(e) => setPractice({ addMax: Number(e.target.value) })}
            >
              {ADD_MAX_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <label htmlFor="sTab">Times tables up to</label>
            <select
              id="sTab"
              value={settings.practice.tableMax}
              onChange={(e) => setPractice({ tableMax: Number(e.target.value) })}
            >
              {TABLE_MAX_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="parents-cols">
          <div className="panel">
            <span className="panel-label">Ranks</span>
            <p className="hint">
              Ranks are earned by passing trials, which are the honest measure. Change one here only
              to fix a mistake or move progress across. Lowering a rank doesn&apos;t take away
              stripes, gold or foil cards, or the overall ninja rank.
            </p>
            <div className="rank-grid">
              {availableModes(settings.ops).map((m) => (
                <label key={m}>
                  {MODE_INFO[m].label}
                  <select
                    value={ranks[m]}
                    onChange={(e) => setRanks((r) => ({ ...r, [m]: Number(e.target.value) }))}
                  >
                    {RANKS.map((x, i) => (
                      <option key={x.name} value={i}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <span className="panel-label">Placement check</span>
            <p className="hint">
              A short, untimed check that suggests a starting rank for each type of maths. It never
              lowers a rank.
            </p>
            <button type="button" className="mini" onClick={saveAndPlace}>
              Save and start a placement check
            </button>
          </div>

          <div className="panel">
            <span className="panel-label">Recent rounds</span>
            <div className="history hint">
              {historyLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <span className="panel-label">Time per question</span>
            {timings.length ? (
              <table className="skills timings">
                <thead>
                  <tr>
                    <th scope="col">Tier</th>
                    <th scope="col">Answered</th>
                    <th scope="col">Right</th>
                    <th scope="col">Average</th>
                  </tr>
                </thead>
                <tbody>
                  {timings.map((t) => (
                    <tr key={t.tier}>
                      <td>Tier {t.tier}</td>
                      <td>{t.answered}</td>
                      <td>{Math.round((t.right / t.answered) * 100)}%</td>
                      <td>{t.seconds === null ? "–" : `${t.seconds} s`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="hint">Shows up after a trial or some practice.</p>
            )}
            <p className="hint">Tier 1 is the easiest questions, tier 5 the hardest.</p>
            {missed.length > 0 && (
              <>
                <span className="panel-label">Most missed</span>
                <p className="hint missed">
                  {missed.map(([k, m]) => `${factText(k)} (${m.n})`).join(", ")}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="panel">
          <span className="panel-label">Dashboard: {name || "Ninja"}</span>
          {signedIn && child.synced && !child.dirty ? (
            <Dashboard childId={child.id} name={name || "Ninja"} version={store.lastSync} />
          ) : skills.length ? (
            <>
              <table className="skills">
                <thead>
                  <tr>
                    <th scope="col">Skill</th>
                    <th scope="col">Tried</th>
                    <th scope="col">Right</th>
                  </tr>
                </thead>
                <tbody>
                  {skills.map(({ t, acc, seen }) => (
                    <tr key={t.id}>
                      <td>
                        {OP_LABELS[t.op]}: {t.label}
                      </td>
                      <td>{seen}</td>
                      <td>{Math.round(acc * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cloudAvailable && !signedIn && (
                <p className="hint">
                  Sign in to see speed, weekly progress and time played across every device.
                </p>
              )}
            </>
          ) : (
            <p className="hint">Shows up after a trial or some practice.</p>
          )}
        </div>

        <div className="parents-cols">
          <AccountPanel
            account={store.account}
            available={cloudAvailable}
            status={status}
            onSignIn={() => {
              saveNow();
              onSignIn();
            }}
            onSyncNow={actions.syncNow}
            onSignedOut={() => {
              actions.signedOut();
              onBack();
            }}
            onFamilyDeleted={() => {
              actions.familyDeleted();
              onBack();
            }}
            onPrivacy={onPrivacy}
            setNotice={setNotice}
          />

          <div className="panel">
            <span className="panel-label">Progress file</span>
            <p className="hint">
              {child.synced
                ? "Export a backup of this child. Importing adds a file's progress to this child."
                : "Export to move progress to another device, or keep a backup."}
            </p>
            <div className="transfer-row">
              <button type="button" className="mini" onClick={() => void doExport()}>
                Export
              </button>
              <button type="button" className="mini" onClick={() => void doCopy()}>
                Copy
              </button>
              <button type="button" className="mini" onClick={() => fileInput.current?.click()}>
                Import file
              </button>
              <button type="button" className="mini" onClick={() => setPasteOpen((o) => !o)}>
                Paste
              </button>
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json,text/plain"
              className="visually-hidden"
              aria-label="Import a progress file"
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
            {pasteOpen && (
              <div className="paste">
                <label htmlFor="pasteBox">Paste progress here</label>
                <textarea
                  id="pasteBox"
                  rows={4}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  spellCheck={false}
                />
                <button type="button" className="mini" onClick={() => applyImport(pasteText)}>
                  Import pasted progress
                </button>
              </div>
            )}
            {!child.synced && (
              <button type="button" className="danger" onClick={doReset}>
                {resetArmed ? "Tap again to reset everything" : "Reset all progress"}
              </button>
            )}
            {kids.length > 1 && (
              <button type="button" className="danger" onClick={doRemove}>
                {removeArmed
                  ? `Tap again to remove ${name || "this child"}`
                  : `Remove ${name || "this child"}`}
              </button>
            )}
            <p className="hint notice" aria-live="polite">
              {notice}
            </p>
          </div>
        </div>

        <button type="button" className="big display" onClick={saveAndBack}>
          Save and go back
        </button>
      </div>

      <Modal open={pinOpen} label="Picture PIN" onClose={() => setPinOpen(false)}>
        <div className="panel pin-modal">
          <PinPad
            title={`Choose ${name || "their"}'s three ninjas`}
            onDone={(pin) => {
              actions.setPin(child.id, pin);
              setPinOpen(false);
              setNotice("PIN saved. Show them their three ninjas.");
            }}
            onCancel={() => setPinOpen(false)}
          />
        </div>
      </Modal>
    </section>
  );
}
